/**
 * Banned contacts: nobody here calls, texts or emails them. The backend
 * refuses every send to them with a 403 `contact_banned` (by hand or
 * automatically); what they send in still arrives. These helpers keep
 * the check and its wording in one place.
 */

/** How a call from a banned contact is handled (Settings → Call routing). */
export const BANNED_CALLER_RING_NO_ANSWER = 'ring_no_answer';
export const BANNED_CALLER_RING_ANSWER = 'ring_answer';
export const BANNED_CALLER_VOICEMAIL = 'voicemail';
export type BannedCallerMode =
    | typeof BANNED_CALLER_RING_NO_ANSWER
    | typeof BANNED_CALLER_RING_ANSWER
    | typeof BANNED_CALLER_VOICEMAIL;

/** `access_state` of a banned contact (0 is active). */
export const CONTACT_BANNED_STATE = -1;
export const CONTACT_BANNED_CODE = 'contact_banned';
const BAN_INCOMING = 'Their calls, texts and emails still reach you.';
export const BAN_EFFECT = `You can't call, text or email them. ${BAN_INCOMING}`;
const BAN_REASON_PROMPT = `Ban this contact? Nobody here will call, text or email them. ${BAN_INCOMING}\n\nReason (optional):`;
const UNKNOWN_BAN_DATE = 'an earlier date';
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** The ban as the backend describes it (`ban` on threads, drafts and incoming calls). */
export interface ContactBan {
    message?: string;
    banned_on: string;
    reason?: string | null;
    contact_id?: number;
}

interface BannableContact {
    access_state?: number | null;
    access_note?: string | null;
    state_updated_at?: string | null;
}

export function isContactBanned(contact?: BannableContact | null): boolean {
    return contact?.access_state === CONTACT_BANNED_STATE;
}

/** The ban on a contact row, dated MM/DD/YYYY from the stored day (as the backend does). */
export function contactBan(contact: BannableContact): ContactBan {
    const match = contact.state_updated_at ? ISO_DATE.exec(contact.state_updated_at) : null;
    return {
        banned_on: match ? `${match[2]}/${match[3]}/${match[1]}` : UNKNOWN_BAN_DATE,
        reason: contact.access_note?.trim() || null,
    };
}

/** "Banned on 11/04/2026 for harassment": tooltips, chips and banners. */
export function banSummary(ban: ContactBan): string {
    return `Banned on ${ban.banned_on}${ban.reason ? ` for ${ban.reason}` : ''}`;
}

/** The tooltip on an action a ban turns off. */
export function bannedActionTitle(contact: BannableContact): string {
    return `${banSummary(contactBan(contact))}. ${BAN_EFFECT}`;
}

/** Asks for the ban's reason: the reason ('' for none), or null when cancelled. */
export function askBanReason(): string | null {
    return window.prompt(BAN_REASON_PROMPT, '');
}

/** The ban behind a refused send (403 `contact_banned`), else null. */
export function banFromError(error: unknown): ContactBan | null {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (!detail || typeof detail !== 'object' || (detail as { code?: string }).code !== CONTACT_BANNED_CODE) return null;
    return detail as ContactBan;
}
